import {
  CssColor,
  CssFont,
  StyleType,
} from '@wix/editor-platform-ooi-metadata-resolver';
import { createStylesParams, StyleParamType } from '@wix/tpa-settings';
import {
  booleanParam,
  colorParam,
  colorParamWithOpacity,
  fontParam,
  numberParam,
} from '../WixServices/styleParamsUtils';

// Page-specific form styles params type
export type IPageStylesParams = {
  pageTitleFont: StyleParamType.Font;
  pageTitleColor: StyleParamType.Color;
  mobilePageTitleFontSize: StyleParamType.Number;
  _pageTitleDisplay: StyleParamType.Boolean;
  _formDisplay: StyleParamType.Boolean;
  formBorderColor: StyleParamType.Color;
  formBorderWidth: StyleParamType.Number;
  formBorderRadius: StyleParamType.Number;
  formFieldBackgroundColor: StyleParamType.Color;
  selectionColor: StyleParamType.Color;
  formHeading1Font: StyleParamType.Font;
  formHeading1Color: StyleParamType.Color;
  mobileFormHeading1FontSize: StyleParamType.Number;
  formHeading2Font: StyleParamType.Font;
  formHeading2Color: StyleParamType.Color;
  mobileFormHeading2FontSize: StyleParamType.Number;
  formTextFont: StyleParamType.Font;
  formTextColor: StyleParamType.Color;
  formFieldsLabelsFont: StyleParamType.Font;
  formFieldsLabelsColor: StyleParamType.Color;
  mobileFormFieldsLabelsFontSize: StyleParamType.Number;
  formFieldsInputTextFont: StyleParamType.Font;
  formFieldsInputTextColor: StyleParamType.Color;
  mobileFormFieldsInputTextFontSize: StyleParamType.Number;
  formButtonFont: StyleParamType.Font;
  formButtonTextColor: StyleParamType.Color;
  formButtonBackgroundColorAndOpacity: StyleParamType.Color;
  formButtonBorderColorAndOpacity: StyleParamType.Color;
  formButtonBorderWidth: StyleParamType.Number;
  formButtonBorderRadius: StyleParamType.Number;
  formButtonHoverTextColor: StyleParamType.Color;
  formButtonHoverBackgroundColorAndOpacity: StyleParamType.Color;
};

// Page-specific form styles params definitions
export const pageStylesParamsDefinitions = {
  // page title styles
  pageTitleFont: fontParam(
    'Page-title',
    { size: 20, htmlTag: 'h1' },
    CssFont.WstHeading2Font,
  ),
  pageTitleColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstHeading2Color,
  ),
  mobilePageTitleFontSize: numberParam(StyleType.fontSize, 20),
  _pageTitleDisplay: booleanParam(true),
  _formDisplay: booleanParam(true),
  // form field styles
  formBorderColor: colorParam(
    StyleType.borderColor,
    'color-5',
    CssColor.WstSystemLine1Color,
  ),
  formBorderWidth: numberParam(StyleType.borderWidth, 1),
  formBorderRadius: numberParam(StyleType.borderRadius, 0),
  formFieldBackgroundColor: colorParam(
    StyleType.backgroundColor,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  selectionColor: colorParam(
    StyleType.color,
    'color-8',
    CssColor.WstAccent1Color,
  ),
  // form text styles
  formHeading1Font: fontParam(
    'Page-title',
    { size: 32, htmlTag: 'h2' },
    CssFont.WstHeading2Font,
  ),
  formHeading1Color: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstHeading2Color,
  ),
  mobileFormHeading1FontSize: numberParam(StyleType.fontSize, 32),
  formHeading2Font: fontParam(
    'Page-title',
    { size: 20, htmlTag: 'h2' },
    CssFont.WstHeading2Font,
  ),
  formHeading2Color: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstHeading2Color,
  ),
  mobileFormHeading2FontSize: numberParam(StyleType.fontSize, 20),
  formTextFont: fontParam(
    'Body-M',
    { size: 16, htmlTag: 'p' },
    CssFont.WstParagraph2Font,
  ),
  formTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  formFieldsLabelsFont: fontParam(
    'Body-M',
    { size: 16, htmlTag: 'p' },
    CssFont.WstParagraph2Font,
  ),
  formFieldsLabelsColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  mobileFormFieldsLabelsFontSize: numberParam(StyleType.fontSize, 14),
  formFieldsInputTextFont: fontParam(
    'Body-M',
    { size: 16, htmlTag: 'p' },
    CssFont.WstParagraph2Font,
  ),
  formFieldsInputTextColor: colorParam(
    StyleType.color,
    'color-5',
    CssColor.WstParagraph2Color,
  ),
  mobileFormFieldsInputTextFontSize: numberParam(StyleType.fontSize, 14),
  // form button styles
  formButtonFont: fontParam(
    'Body-M',
    { size: 16 },
    CssFont.WstParagraph2Font,
  ),
  formButtonTextColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstButtonPrimaryColor,
  ),
  formButtonBackgroundColorAndOpacity: colorParam(
    StyleType.backgroundColor,
    'color-8',
    CssColor.WstButtonPrimaryBackgroundColor,
  ),
  formButtonBorderColorAndOpacity: colorParam(
    StyleType.borderColor,
    'color-8',
    CssColor.WstButtonPrimaryBorderRightColor,
  ),
  formButtonBorderWidth: numberParam(StyleType.borderWidth, 1),
  formButtonBorderRadius: numberParam(StyleType.borderRadius, 0),
  formButtonHoverTextColor: colorParam(
    StyleType.color,
    'color-1',
    CssColor.WstPrimaryBackgroundColor,
  ),
  formButtonHoverBackgroundColorAndOpacity: colorParamWithOpacity(
    StyleType.backgroundColor,
    'color-8',
    1,
    CssColor.WstButtonPrimaryBackgroundColor,
  ),
};

// Create page-specific params (like bookings-calendar-widget pattern)
const pageStylesParams = createStylesParams<IPageStylesParams>(
  pageStylesParamsDefinitions,
);

export default pageStylesParams;
